// CTODashboard - Cockpit CTO IA dans OmniHub
// Interface de gouvernance technique et d'audit
import { useState, useEffect, useCallback } from "react";
import {
  Shield,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Play,
  FileText,
  Server,
  Database,
  Cloud,
  Lock,
  Eye,
  Zap,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  ChevronDown,
  Search,
  Download,
  Terminal,
  Settings,
  ExternalLink,
  AlertCircle,
  Info,
  Clock,
  BarChart3,
  Cpu,
  HardDrive,
  Wifi,
  WifiOff,
  Key,
  FileJson,
  Code,
  ClipboardList,
  TestTube,
  ListChecks,
  PlayCircle,
  MessageSquare,
  Send,
  Brain,
  Mail
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Progress } from "../../components/ui/progress";
import CxOChatPanel from "./CxOChatPanel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Maturité styles
const maturityStyles = {
  "production": "bg-green-500/20 text-green-400 border-green-500/30",
  "v1_exploitable": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "v1_fragile": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  "prototype": "bg-orange-500/20 text-orange-400 border-orange-500/30",
  "non_gouvernable": "bg-red-500/20 text-red-400 border-red-500/30"
};

const maturityLabels = {
  "production": "Prêt Production",
  "v1_exploitable": "V1 Exploitable",
  "v1_fragile": "V1 Fragile",
  "prototype": "Prototype",
  "non_gouvernable": "Non Gouvernable"
};

// Configuration des modules à tester
const MODULES_CONFIG = {
  management: {
    label: "Management Hub",
    modules: [
      { id: "users", label: "Utilisateurs", endpoint: "/api/management/users" },
      { id: "roles", label: "Rôles", endpoint: "/api/management/roles" },
      { id: "audit", label: "Audit", endpoint: "/api/management/audit?limit=5" },
      { id: "rules", label: "Règles", endpoint: "/api/management/rules" },
      { id: "integrations", label: "Intégrations", endpoint: "/api/connections" }
    ]
  },
  marketing: {
    label: "Marketing Hub",
    modules: [
      { id: "planning", label: "Planning Éditorial", endpoint: "/api/marketing/editorial" },
      { id: "seo", label: "SEO Manager", endpoint: "/api/marketing/seo/sites" },
      { id: "content", label: "Content Factory", endpoint: "/api/marketing/contents" }
    ]
  },
  communication: {
    label: "Communication Hub",
    modules: [
      { id: "audrey_runtime", label: "Audrey Runtime", endpoint: "/api/audrey/runtime/status" },
      { id: "audrey_config", label: "Audrey Config", endpoint: "/api/audrey/config-raw" },
      { id: "calls", label: "Appels", endpoint: "/api/calls?limit=5" },
      { id: "whatsapp", label: "WhatsApp", endpoint: "/api/whatsapp/health" },
      { id: "email", label: "Email", endpoint: "/api/mail/senders" },
      { id: "sms", label: "SMS", endpoint: "/api/communication/stats" }
    ]
  },
  operation: {
    label: "Opération Hub",
    modules: [
      { id: "tickets", label: "Tickets", endpoint: "/api/tickets" },
      { id: "missions", label: "Missions", endpoint: "/api/operation/missions" },
      { id: "dossiers", label: "Dossiers", endpoint: "/api/operation/dossiers" },
      { id: "clients", label: "Clients", endpoint: "/api/commercial/clients" }
    ]
  }
};

// Dépendances externes
const EXTERNAL_DEPS = [
  { id: "twilio_voice", label: "Twilio Voice", checkEndpoint: "/api/twilio/phone-status" },
  { id: "twilio_sms", label: "Twilio SMS", checkEndpoint: "/api/communication/smtp-status" },
  { id: "elevenlabs", label: "ElevenLabs TTS", checkEndpoint: "/api/audrey/runtime/status" },
  { id: "gsc", label: "Google Search Console", checkEndpoint: "/api/marketing/seo/sites" },
  { id: "mongodb", label: "MongoDB", checkEndpoint: "/api/health" }
];

// Composant pour afficher le statut d'un module avec vue métier
const ModuleStatusRow = ({ module, status, usageNote, blockingItems }) => {
  const [expanded, setExpanded] = useState(false);
  
  const statusConfig = {
    exploitable: {
      icon: CheckCircle,
      color: "text-green-400",
      bg: "bg-green-500/10",
      border: "border-green-500/30",
      label: "Exploitable"
    },
    friction: {
      icon: AlertTriangle,
      color: "text-yellow-400",
      bg: "bg-yellow-500/10",
      border: "border-yellow-500/30",
      label: "Avec friction"
    },
    non_gouvernable: {
      icon: Lock,
      color: "text-orange-400",
      bg: "bg-orange-500/10",
      border: "border-orange-500/30",
      label: "Non gouvernable"
    },
    a_corriger: {
      icon: XCircle,
      color: "text-red-400",
      bg: "bg-red-500/10",
      border: "border-red-500/30",
      label: "À corriger"
    }
  };
  
  const config = statusConfig[status];
  const Icon = config.icon;
  const hasBlockingItems = blockingItems && blockingItems.length > 0;
  
  return (
    <div className={`rounded-lg border ${config.border} ${config.bg}`}>
      <div 
        className={`flex items-center justify-between p-3 ${hasBlockingItems ? 'cursor-pointer' : ''}`}
        onClick={() => hasBlockingItems && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${config.color}`} />
          <div>
            <p className="font-medium text-white">{module}</p>
            <p className="text-xs text-zinc-400">{usageNote}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`${config.bg} ${config.color} border ${config.border}`}>
            {config.label}
          </Badge>
          {hasBlockingItems && (
            <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          )}
        </div>
      </div>
      
      {expanded && hasBlockingItems && (
        <div className="px-3 pb-3 pt-0">
          <div className="p-2 bg-zinc-800/50 rounded mt-1">
            <p className="text-xs font-medium text-zinc-400 mb-2">Points bloquants :</p>
            <ul className="space-y-1">
              {blockingItems.map((item, idx) => (
                <li key={idx} className="text-xs text-zinc-300 flex items-start gap-2">
                  <span className="text-red-400 mt-0.5">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default function CTODashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  
  // Test results
  const [testResults, setTestResults] = useState({});
  const [depResults, setDepResults] = useState({});
  const [lastTestTime, setLastTestTime] = useState(null);
  
  // Audit data
  const [auditReport, setAuditReport] = useState(null);
  
  // Stats
  const [globalStats, setGlobalStats] = useState({
    totalModules: 0,
    working: 0,
    partial: 0,
    failed: 0
  });

  // Custom test
  const [customEndpoint, setCustomEndpoint] = useState("");
  const [customResult, setCustomResult] = useState(null);
  const [customTesting, setCustomTesting] = useState(false);

  // Functional Tests
  const [functionalTestDefs, setFunctionalTestDefs] = useState({});
  const [functionalTestResults, setFunctionalTestResults] = useState([]);
  const [functionalTestSummary, setFunctionalTestSummary] = useState({});
  const [functionalTestCoverage, setFunctionalTestCoverage] = useState({});
  const [runningFunctionalTests, setRunningFunctionalTests] = useState(false);
  const [selectedModule, setSelectedModule] = useState("all");

  // IMAP Reliability Tests (CTO)
  const [imapReliabilityResults, setImapReliabilityResults] = useState(null);
  const [runningImapTests, setRunningImapTests] = useState(false);

  // Validation Status (CTO)
  const [validationStatus, setValidationStatus] = useState(null);

  // Governance Summary (Vue stratégique)
  const [governanceData, setGovernanceData] = useState(null);
  const [loadingGovernance, setLoadingGovernance] = useState(false);

  // Load validation status
  const loadValidationStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/cto/functional-tests/validation-status`);
      setValidationStatus(response.data);
    } catch (error) {
      console.log("Validation status not available");
    }
  }, []);

  // Load governance summary
  const loadGovernanceSummary = useCallback(async () => {
    setLoadingGovernance(true);
    try {
      const response = await axios.get(`${API}/api/cto/functional-tests/governance-summary`);
      setGovernanceData(response.data);
    } catch (error) {
      console.log("Governance summary not available");
    } finally {
      setLoadingGovernance(false);
    }
  }, []);

  // Load audit report
  const loadAuditReport = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/cto/audit-report`);
      setAuditReport(response.data);
    } catch (error) {
      // Try to load from memory file
      console.log("Audit report not available via API");
    }
  }, []);

  // Load functional test definitions
  const loadFunctionalTestDefs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/cto/functional-tests/definitions`);
      setFunctionalTestDefs(response.data.modules || {});
    } catch (error) {
      console.log("Functional test definitions not available");
    }
  }, []);

  // Load functional test results and coverage
  const loadFunctionalTestResults = useCallback(async () => {
    try {
      const [resultsRes, coverageRes] = await Promise.all([
        axios.get(`${API}/api/cto/functional-tests/results`),
        axios.get(`${API}/api/cto/functional-tests/coverage`)
      ]);
      
      setFunctionalTestResults(resultsRes.data.latest_by_test || []);
      setFunctionalTestSummary(resultsRes.data.summary || {});
      setFunctionalTestCoverage(coverageRes.data || {});
    } catch (error) {
      console.log("Functional test results not available");
    }
  }, []);

  // Run all functional tests
  const runAllFunctionalTests = useCallback(async () => {
    setRunningFunctionalTests(true);
    try {
      const response = await axios.post(`${API}/api/cto/functional-tests/run-all`);
      setFunctionalTestSummary(response.data.summary || {});
      await loadFunctionalTestResults();
      toast.success(`Tests fonctionnels terminés: ${response.data.summary?.ok || 0}/${response.data.summary?.total || 0} OK`);
    } catch (error) {
      toast.error("Erreur lors des tests fonctionnels");
    } finally {
      setRunningFunctionalTests(false);
    }
  }, [loadFunctionalTestResults]);

  // Run single functional test
  const runSingleTest = async (testId) => {
    try {
      await axios.post(`${API}/api/cto/functional-tests/run/${testId}`);
      await loadFunctionalTestResults();
      toast.success("Test exécuté");
    } catch (error) {
      toast.error("Erreur lors du test");
    }
  };

  // Run IMAP Reliability Tests (CTO)
  const runImapReliabilityTests = useCallback(async () => {
    setRunningImapTests(true);
    try {
      const response = await axios.post(`${API}/api/cto/functional-tests/run-imap-reliability`);
      setImapReliabilityResults(response.data);
      await loadFunctionalTestResults();
      const summary = response.data.summary || {};
      toast.success(`Tests IMAP terminés: ${summary.ok || 0}/${summary.total || 0} OK`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error(`Erreur tests IMAP: ${errorMsg}`);
      setImapReliabilityResults({ error: errorMsg, results: [] });
    } finally {
      setRunningImapTests(false);
    }
  }, [loadFunctionalTestResults]);

  // Run all tests
  const runAllTests = useCallback(async () => {
    setTesting(true);
    const results = {};
    let working = 0, partial = 0, failed = 0, total = 0;

    for (const [hubId, hub] of Object.entries(MODULES_CONFIG)) {
      results[hubId] = {};
      
      for (const module of hub.modules) {
        total++;
        try {
          const start = Date.now();
          const response = await axios.get(`${API}${module.endpoint}`, { timeout: 10000 });
          const duration = Date.now() - start;
          
          results[hubId][module.id] = {
            status: "ok",
            duration,
            data: Array.isArray(response.data) ? response.data.length : (response.data ? 1 : 0),
            message: `OK (${duration}ms)`
          };
          working++;
        } catch (error) {
          if (error.response?.status === 404) {
            results[hubId][module.id] = {
              status: "missing",
              message: "Endpoint non trouvé (404)"
            };
            failed++;
          } else if (error.code === 'ECONNABORTED') {
            results[hubId][module.id] = {
              status: "timeout",
              message: "Timeout (>10s)"
            };
            partial++;
          } else {
            const errorMsg = error.response?.data?.detail;
            results[hubId][module.id] = {
              status: "error",
              message: typeof errorMsg === 'string' ? errorMsg : (error.message || "Erreur inconnue")
            };
            failed++;
          }
        }
      }
    }

    setTestResults(results);
    setGlobalStats({ totalModules: total, working, partial, failed });
    setLastTestTime(new Date().toISOString());
    setTesting(false);
    setLoading(false);
    
    toast.success(`Tests terminés: ${working}/${total} OK`);
  }, []);

  // Check external dependencies
  const checkDependencies = useCallback(async () => {
    const results = {};
    
    for (const dep of EXTERNAL_DEPS) {
      try {
        const response = await axios.get(`${API}${dep.checkEndpoint}`, { timeout: 5000 });
        results[dep.id] = {
          status: "ok",
          data: response.data
        };
      } catch (error) {
        results[dep.id] = {
          status: "error",
          message: error.message
        };
      }
    }
    
    setDepResults(results);
  }, []);

  // Custom endpoint test
  const testCustomEndpoint = async () => {
    if (!customEndpoint) return;
    
    setCustomTesting(true);
    setCustomResult(null);
    
    try {
      const start = Date.now();
      const url = customEndpoint.startsWith('/') ? `${API}${customEndpoint}` : customEndpoint;
      const response = await axios.get(url, { timeout: 15000 });
      const duration = Date.now() - start;
      
      setCustomResult({
        status: "ok",
        duration,
        statusCode: response.status,
        data: response.data,
        headers: response.headers
      });
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      setCustomResult({
        status: "error",
        statusCode: error.response?.status,
        message: typeof errorDetail === 'string' ? errorDetail : (error.message || "Erreur"),
        data: error.response?.data
      });
    } finally {
      setCustomTesting(false);
    }
  };

  useEffect(() => {
    runAllTests();
    checkDependencies();
    loadAuditReport();
    loadFunctionalTestDefs();
    loadFunctionalTestResults();
    loadValidationStatus();
    loadGovernanceSummary();
  }, [runAllTests, checkDependencies, loadAuditReport, loadFunctionalTestDefs, loadFunctionalTestResults, loadValidationStatus, loadGovernanceSummary]);

  // Render status icon
  const StatusIcon = ({ status }) => {
    switch (status) {
      case "ok": return <CheckCircle className="w-4 h-4 text-green-400" />;
      case "partial": 
      case "timeout": return <AlertCircle className="w-4 h-4 text-yellow-400" />;
      case "missing":
      case "error": return <XCircle className="w-4 h-4 text-red-400" />;
      default: return <Info className="w-4 h-4 text-zinc-400" />;
    }
  };

  return (
    <div className="h-full" data-testid="cto-dashboard">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="bg-zinc-900/50 border border-white/10">
            <TabsTrigger 
              value="overview" 
              className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400"
            >
              <Activity className="w-4 h-4 mr-2" />
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger 
              value="functional" 
              className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400"
            >
              <TestTube className="w-4 h-4 mr-2" />
              Tests Fonctionnels
            </TabsTrigger>
            <TabsTrigger 
              value="governance" 
              className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400"
            >
              <Lock className="w-4 h-4 mr-2" />
              Gouvernance
            </TabsTrigger>
            <TabsTrigger 
              value="chat" 
              className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Mode Conversationnel
            </TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-3">
            {lastTestTime && (
              <span className="text-xs text-zinc-500">
                Dernier test: {new Date(lastTestTime).toLocaleTimeString('fr-FR')}
              </span>
            )}
            <Button
              onClick={() => { runAllTests(); checkDependencies(); }}
              disabled={testing}
              size="sm"
              className="bg-red-600 hover:bg-red-700"
            >
              {testing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Lancer tests
            </Button>
          </div>
        </div>

        {/* TAB: Overview */}
        <TabsContent value="overview" className="flex-1 mt-0 space-y-6">
          {/* Header - Toujours visible */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                Vue d'ensemble
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">CTO IA</Badge>
              </h1>
              <p className="text-zinc-400 text-sm">Gouvernance technique & stabilité des modules</p>
            </div>
          </div>

          {/* STATUT VALIDATION GLOBAL CTO */}
          {validationStatus && (
            <Card className={`border-2 ${validationStatus.is_valid 
              ? 'bg-green-500/5 border-green-500/30' 
              : 'bg-red-500/5 border-red-500/30'}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {validationStatus.is_valid ? (
                    <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center">
                      <AlertTriangle className="w-8 h-8 text-red-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h2 className={`text-xl font-bold ${validationStatus.is_valid ? 'text-green-400' : 'text-red-400'}`}>
                      {validationStatus.is_valid 
                        ? '✅ VALIDATION CTO : POSSIBLE' 
                        : '🛑 VALIDATION CTO : BLOQUÉE'}
                    </h2>
                    <p className="text-sm text-zinc-400 mt-1">
                      {validationStatus.is_valid 
                        ? 'Tous les invariants des modules opérationnels sont validés.'
                        : `${validationStatus.blocking_issues?.length || 0} module(s) bloquant(s) - La V1 ne peut pas être déclarée terminée.`}
                    </p>
                    
                    {/* Résumé */}
                    <div className="flex items-center gap-6 mt-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span className="text-green-400 font-bold">{validationStatus.summary?.ok || 0}</span>
                        <span className="text-xs text-zinc-500">OK</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-400" />
                        <span className="text-red-400 font-bold">{validationStatus.summary?.ko || 0}</span>
                        <span className="text-xs text-zinc-500">KO</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-zinc-400" />
                        <span className="text-zinc-400 font-bold">{validationStatus.summary?.non_implemente || 0}</span>
                        <span className="text-xs text-zinc-500">NON IMPL.</span>
                      </div>
                      <div className="ml-auto">
                        <Badge className={`${validationStatus.summary?.validation_percent === 100 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-red-500/20 text-red-400'}`}>
                          {validationStatus.summary?.validation_percent || 0}% validé
                        </Badge>
                      </div>
                    </div>

                    {/* Liste des blocages */}
                    {validationStatus.blocking_issues?.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">Modules bloquants :</p>
                        {validationStatus.blocking_issues.map((issue, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                            <XCircle className="w-4 h-4 text-red-400" />
                            <span className="text-sm text-white font-medium">{issue.module_label}</span>
                            <Badge className="bg-zinc-700 text-zinc-300 text-xs">{issue.hub}</Badge>
                            <span className="text-xs text-red-400 ml-auto">
                              {issue.ko_count > 0 && `${issue.ko_count} KO`}
                              {issue.ko_count > 0 && issue.non_impl_count > 0 && ' • '}
                              {issue.non_impl_count > 0 && `${issue.non_impl_count} NON IMPL.`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contenu - Avec état de chargement */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-red-400" />
              <span className="ml-3 text-zinc-400">Chargement des données...</span>
            </div>
          ) : (
            <>
              {/* Global Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Modules</p>
                    <p className="text-2xl font-bold text-white mt-1">{globalStats.totalModules}</p>
                  </div>
                  <div className="p-3 bg-zinc-700/30 rounded-lg">
                    <Cpu className="w-6 h-6 text-zinc-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Fonctionnels</p>
                    <p className="text-2xl font-bold text-green-400 mt-1">{globalStats.working}</p>
                  </div>
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Partiels</p>
                    <p className="text-2xl font-bold text-yellow-400 mt-1">{globalStats.partial}</p>
                  </div>
                  <div className="p-3 bg-yellow-500/10 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-yellow-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">En erreur</p>
                    <p className="text-2xl font-bold text-red-400 mt-1">{globalStats.failed}</p>
                  </div>
                  <div className="p-3 bg-red-500/10 rounded-lg">
                    <XCircle className="w-6 h-6 text-red-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {Object.entries(MODULES_CONFIG).map(([hubId, hub]) => (
              <Card key={hubId} className="bg-zinc-900 border-zinc-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span>{hub.label}</span>
                    <Badge variant="outline" className="border-zinc-600">
                      {testResults[hubId] ? 
                        `${Object.values(testResults[hubId]).filter(r => r.status === 'ok').length}/${hub.modules.length}` 
                        : '...'}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {hub.modules.map(module => {
                      const result = testResults[hubId]?.[module.id];
                      return (
                        <div 
                          key={module.id}
                          className="flex items-center justify-between p-2 bg-zinc-800/50 rounded"
                        >
                          <div className="flex items-center gap-2">
                            {result ? <StatusIcon status={result.status} /> : <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />}
                            <span className="text-sm text-zinc-300">{module.label}</span>
                          </div>
                          <span className="text-xs text-zinc-500">
                            {result?.message || 'En cours...'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
            </>
          )}
        </TabsContent>

        {/* TAB: Functional Tests */}
        <TabsContent value="functional" className="flex-1 mt-0 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                <TestTube className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  Tests Fonctionnels
                  <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">Journal</Badge>
                </h1>
                <p className="text-zinc-400 text-sm">Actions utilisateur réelles • Tracé horodaté</p>
              </div>
            </div>
            
            <Button
              onClick={runAllFunctionalTests}
              disabled={runningFunctionalTests}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {runningFunctionalTests ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <PlayCircle className="w-4 h-4 mr-2" />
              )}
              Lancer tous les tests
            </Button>
          </div>

          {/* Stats badges */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-green-400 font-medium">{functionalTestSummary.ok || 0}</span>
              <span className="text-xs text-zinc-400">OK</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
              <XCircle className="w-4 h-4 text-red-400" />
              <span className="text-red-400 font-medium">{functionalTestSummary.ko || 0}</span>
              <span className="text-xs text-zinc-400">KO</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 font-medium">{functionalTestSummary.partial || 0}</span>
              <span className="text-xs text-zinc-400">Partiel</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg">
              <Info className="w-4 h-4 text-zinc-400" />
              <span className="text-zinc-400 font-medium">{functionalTestSummary.not_implemented || 0}</span>
              <span className="text-xs text-zinc-500">Non impl.</span>
            </div>
          </div>

          {/* Coverage Info */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-cyan-400" />
                Couverture des Tests Fonctionnels
              </CardTitle>
              <CardDescription>
                {functionalTestCoverage.tested || 0} / {functionalTestCoverage.total || 0} actions testées 
                ({functionalTestCoverage.coverage_percent || 0}%)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Progress 
                value={functionalTestCoverage.coverage_percent || 0} 
                className="h-2 bg-zinc-800"
              />
            </CardContent>
          </Card>

          {/* ================================================================ */}
          {/* SECTION CTO - Tests de Fiabilité IMAP */}
          {/* ================================================================ */}
          <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-red-500" data-testid="imap-reliability-section">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      Fiabilité IMAP
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                        CTO
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      Tests de fiabilité critique • Responsabilité Direction Technique
                    </CardDescription>
                  </div>
                </div>
                <Button
                  onClick={runImapReliabilityTests}
                  disabled={runningImapTests}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="run-imap-tests-btn"
                >
                  {runningImapTests ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <PlayCircle className="w-4 h-4 mr-2" />
                  )}
                  Lancer tests IMAP
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Résumé des tests IMAP avec compteur NON IMPLÉMENTÉ */}
              {(() => {
                const tests = [
                  "imap_sync_idempotence", "imap_no_duplicates", "imap_deleted_cleanup",
                  "imap_source_coherence", "imap_real_move", "imap_move_rollback",
                  "imap_connection_closure", "imap_timeout_strict", "imap_error_recovery"
                ];
                const results = tests.map(id => {
                  const r = imapReliabilityResults?.results?.find(x => x.test_id === id) || 
                            functionalTestResults.find(x => x.test_id === id);
                  return r?.status;
                });
                const okCount = results.filter(s => s === "ok").length;
                const koCount = results.filter(s => s === "ko").length;
                const nonImplCount = results.filter(s => s !== "ok" && s !== "ko").length;
                const timestamp = imapReliabilityResults?.timestamp;
                
                return (
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-green-400 font-medium">{okCount}</span>
                      <span className="text-xs text-zinc-400">OK</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <XCircle className="w-4 h-4 text-red-400" />
                      <span className="text-red-400 font-medium">{koCount}</span>
                      <span className="text-xs text-zinc-400">KO</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 bg-zinc-700/50 border border-zinc-600/30 rounded-lg">
                      <XCircle className="w-4 h-4 text-zinc-400" />
                      <span className="text-zinc-400 font-medium">{nonImplCount}</span>
                      <span className="text-xs text-zinc-500">NON IMPL.</span>
                    </div>
                    {timestamp && (
                      <span className="text-xs text-zinc-500 ml-auto">
                        Exécuté le {new Date(timestamp).toLocaleString('fr-FR')}
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* Message d'erreur si échec */}
              {imapReliabilityResults?.error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-400" />
                    <span className="text-red-400 font-medium">Erreur: {imapReliabilityResults.error}</span>
                  </div>
                </div>
              )}

              {/* Tableau des résultats IMAP */}
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400 w-1/4">Test</TableHead>
                    <TableHead className="text-zinc-400 w-1/3">Invariant attendu</TableHead>
                    <TableHead className="text-zinc-400 w-1/4">Résultat réel</TableHead>
                    <TableHead className="text-zinc-400 w-24">Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Définition des tests IMAP */}
                  {[
                    { id: "imap_sync_idempotence", label: "Idempotence de la synchronisation", expected: "Deux syncs consécutifs = même état, aucun doublon" },
                    { id: "imap_no_duplicates", label: "Pas de doublons", expected: "Aucun email en double après synchronisation" },
                    { id: "imap_deleted_cleanup", label: "Nettoyage emails supprimés", expected: "Emails absents du serveur sont supprimés localement" },
                    { id: "imap_source_coherence", label: "Cohérence source ↔ base", expected: "Nombre d'emails local = nombre sur serveur IMAP" },
                    { id: "imap_real_move", label: "Déplacement réel sur IMAP", expected: "Un déplacement OmniHub modifie le serveur IMAP" },
                    { id: "imap_move_rollback", label: "Rollback si échec", expected: "Si IMAP échoue, la BDD n'est pas modifiée" },
                    { id: "imap_connection_closure", label: "Fermeture des connexions", expected: "Chaque connexion IMAP est fermée immédiatement après l'opération" },
                    { id: "imap_timeout_strict", label: "Timeout strict (30s)", expected: "Les connexions IMAP ont un timeout ≤ 30 secondes" },
                    { id: "imap_error_recovery", label: "Reprise sur erreur", expected: "Une erreur réseau ne corrompt pas la base" }
                  ].map((test) => {
                    const result = imapReliabilityResults?.results?.find(r => r.test_id === test.id);
                    const fromFunctionalResults = functionalTestResults.find(r => r.test_id === test.id);
                    const finalResult = result || fromFunctionalResults;
                    
                    // Statuts CTO stricts : OK, KO, NON IMPLÉMENTÉ (pas de "Partiel", pas de "Non testé")
                    const statusConfig = {
                      ok: { icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/20", label: "OK", blocking: false },
                      ko: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/20", label: "KO", blocking: true },
                      non_implemente: { icon: XCircle, color: "text-zinc-400", bg: "bg-zinc-700", label: "NON IMPL.", blocking: true }
                    };
                    // Tout ce qui n'est pas OK ou KO explicite = NON IMPLÉMENTÉ (bloquant)
                    const status = (finalResult?.status === "ok" || finalResult?.status === "ko") 
                      ? finalResult.status 
                      : "non_implemente";
                    const config = statusConfig[status] || statusConfig.non_implemente;
                    const StatusIconComponent = config.icon;
                    
                    return (
                      <TableRow key={test.id} className={`border-zinc-800 ${config.blocking ? 'bg-red-500/5' : ''}`} data-testid={`imap-test-${test.id}`}>
                        <TableCell className="font-medium text-white text-sm">
                          {test.label}
                          {config.blocking && (
                            <span className="ml-2 text-xs text-red-400">⚠ BLOQUANT</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-zinc-400">
                          {test.expected}
                        </TableCell>
                        <TableCell className="text-xs text-zinc-300">
                          {finalResult?.actual_result || (status === "non_implemente" ? "Invariant non couvert" : "-")}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${config.bg} ${config.color} border-none`}>
                            <StatusIconComponent className="w-3 h-3 mr-1" />
                            {config.label}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Alerte de blocage CTO */}
              {(() => {
                const tests = [
                  "imap_sync_idempotence", "imap_no_duplicates", "imap_deleted_cleanup",
                  "imap_source_coherence", "imap_real_move", "imap_move_rollback",
                  "imap_connection_closure", "imap_timeout_strict", "imap_error_recovery"
                ];
                const results = tests.map(id => {
                  const r = imapReliabilityResults?.results?.find(x => x.test_id === id) || 
                            functionalTestResults.find(x => x.test_id === id);
                  return r?.status;
                });
                const okCount = results.filter(s => s === "ok").length;
                const koCount = results.filter(s => s === "ko").length;
                const nonImplCount = results.filter(s => s !== "ok" && s !== "ko").length;
                const isBlocked = koCount > 0 || nonImplCount > 0;
                
                return isBlocked ? (
                  <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-red-400 mt-0.5" />
                      <div>
                        <p className="font-bold text-red-400">🛑 VALIDATION BLOQUÉE</p>
                        <p className="text-sm text-zinc-300 mt-1">
                          Le Centre Email ne peut pas être déclaré fiable.
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs">
                          <span className="text-green-400">{okCount} OK</span>
                          <span className="text-red-400">{koCount} KO</span>
                          <span className="text-zinc-400">{nonImplCount} NON IMPLÉMENTÉ</span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-2">
                          Action requise : corriger les KO et implémenter les tests manquants avant validation V1.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-400 mt-0.5" />
                      <div>
                        <p className="font-bold text-green-400">✅ VALIDATION POSSIBLE</p>
                        <p className="text-sm text-zinc-300 mt-1">
                          Tous les invariants IMAP sont couverts et validés.
                        </p>
                        <p className="text-xs text-zinc-500 mt-2">
                          Le Centre Email peut être considéré fiable pour le COO.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Info CTO */}
              <div className="mt-4 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                <div className="flex items-start gap-2">
                  <Shield className="w-5 h-5 text-red-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-zinc-300 text-sm">Doctrine CTO</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      Un test KO ou non implémenté = invariant non couvert = validation bloquée.
                      Le COO ne peut pas s'appuyer sur un module dont les tests CTO ne sont pas 100% au vert.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tests by Module */}
          <div className="space-y-4">
            {Object.entries(functionalTestDefs).map(([moduleId, moduleData]) => {
              const moduleResults = functionalTestResults.filter(r => r.module === moduleId);
              const okCount = moduleResults.filter(r => r.status === "ok").length;
              const koCount = moduleResults.filter(r => r.status === "ko").length;
              
              return (
                <Card key={moduleId} className="bg-zinc-900 border-zinc-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-purple-400" />
                        {moduleData.module_label}
                      </span>
                      <div className="flex items-center gap-2">
                        {okCount > 0 && (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            {okCount} OK
                          </Badge>
                        )}
                        {koCount > 0 && (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                            {koCount} KO
                          </Badge>
                        )}
                        <Badge variant="outline" className="border-zinc-600">
                          {moduleData.tests?.length || 0} tests
                        </Badge>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800">
                          <TableHead className="text-zinc-400 w-1/4">Action</TableHead>
                          <TableHead className="text-zinc-400 w-1/3">Résultat attendu</TableHead>
                          <TableHead className="text-zinc-400 w-1/4">Résultat réel</TableHead>
                          <TableHead className="text-zinc-400 w-24">Statut</TableHead>
                          <TableHead className="text-zinc-400 w-20">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(moduleData.tests || []).map((test) => {
                          const result = functionalTestResults.find(r => r.test_id === test.id);
                          const statusConfig = {
                            ok: { icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/20", label: "OK" },
                            ko: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/20", label: "KO" },
                            partiel: { icon: AlertTriangle, color: "text-yellow-400", bg: "bg-yellow-500/20", label: "Partiel" },
                            non_implemente: { icon: Info, color: "text-zinc-400", bg: "bg-zinc-700", label: "Non impl." },
                            non_teste: { icon: Clock, color: "text-zinc-500", bg: "bg-zinc-800", label: "Non testé" }
                          };
                          const status = result?.status || "non_teste";
                          const config = statusConfig[status] || statusConfig.non_teste;
                          const StatusIcon = config.icon;
                          
                          return (
                            <TableRow key={test.id} className="border-zinc-800">
                              <TableCell className="font-medium text-white text-sm">
                                {test.action_label}
                              </TableCell>
                              <TableCell className="text-xs text-zinc-400">
                                {test.expected_result}
                              </TableCell>
                              <TableCell className="text-xs text-zinc-300">
                                {result?.actual_result || "-"}
                              </TableCell>
                              <TableCell>
                                <Badge className={`${config.bg} ${config.color} border-none`}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {config.label}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => runSingleTest(test.id)}
                                  className="h-7 px-2"
                                >
                                  <Play className="w-3 h-3" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Journal des tests */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-400" />
                Journal des Tests (7 derniers jours)
              </CardTitle>
              <CardDescription>
                Historique horodaté des tests fonctionnels exécutés
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                {functionalTestResults.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <TestTube className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun test fonctionnel exécuté</p>
                    <p className="text-xs mt-1">Cliquez sur "Lancer tous les tests" pour commencer</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {functionalTestResults.map((result, idx) => {
                      const statusConfig = {
                        ok: { color: "border-l-green-500 bg-green-500/5" },
                        ko: { color: "border-l-red-500 bg-red-500/5" },
                        partiel: { color: "border-l-yellow-500 bg-yellow-500/5" },
                        non_implemente: { color: "border-l-zinc-500 bg-zinc-800/50" },
                        non_teste: { color: "border-l-zinc-600 bg-zinc-800/30" }
                      };
                      const config = statusConfig[result.status] || statusConfig.non_teste;
                      
                      return (
                        <div 
                          key={idx}
                          className={`p-3 border-l-4 rounded-r-lg ${config.color}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-white">
                                {result.action_label}
                              </p>
                              <p className="text-xs text-zinc-400">
                                {result.module_label} • {result.actual_result}
                              </p>
                            </div>
                            <div className="text-right">
                              <Badge variant="outline" className="border-zinc-600 text-xs">
                                {result.status?.toUpperCase()}
                              </Badge>
                              {result.timestamp && (
                                <p className="text-xs text-zinc-500 mt-1">
                                  {new Date(result.timestamp).toLocaleString('fr-FR')}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Governance - Vue Stratégique CTO */}
        <TabsContent value="governance" className="flex-1 mt-0 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  Gouvernance
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">CTO/COO</Badge>
                </h1>
                <p className="text-zinc-400 text-sm">Classification des modules selon la doctrine CTO</p>
              </div>
            </div>
            <Button
              onClick={loadGovernanceSummary}
              disabled={loadingGovernance}
              size="sm"
              className="bg-amber-600 hover:bg-amber-700"
            >
              {loadingGovernance ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Actualiser
            </Button>
          </div>

          {/* Doctrine Rappel */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-amber-400 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-400 mb-1">Doctrine CTO/COO</p>
                  <p className="text-zinc-400">
                    <span className="text-red-400">🔴 À corriger</span> = invariant non respecté → bloquant V1 → responsabilité CTO<br/>
                    <span className="text-yellow-400">🟡 Avec friction</span> = invariant OK mais usage imparfait → acceptable V1 → backlog produit<br/>
                    <span className="text-zinc-500">On ne requalifie jamais un "à corriger" en "friction" pour faire passer une validation.</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Statut V1 Global */}
          {governanceData && (
            <Card className={`border-2 ${governanceData.v1_validation?.is_valid 
              ? 'bg-green-500/5 border-green-500/30' 
              : 'bg-red-500/5 border-red-500/30'}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {governanceData.v1_validation?.is_valid ? (
                      <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center">
                        <CheckCircle className="w-8 h-8 text-green-400" />
                      </div>
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center">
                        <XCircle className="w-8 h-8 text-red-400" />
                      </div>
                    )}
                    <div>
                      <h2 className={`text-xl font-bold ${governanceData.v1_validation?.is_valid ? 'text-green-400' : 'text-red-400'}`}>
                        {governanceData.v1_validation?.is_valid 
                          ? '✅ V1 VALIDABLE' 
                          : '🛑 V1 NON VALIDABLE'}
                      </h2>
                      <p className="text-sm text-zinc-400">
                        {governanceData.v1_validation?.is_valid 
                          ? 'Tous les invariants V1 sont respectés.'
                          : `${governanceData.v1_validation?.blocking_modules?.length || 0} module(s) bloquant(s)`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-4xl font-bold ${governanceData.v1_validation?.is_valid ? 'text-green-400' : 'text-red-400'}`}>
                      {governanceData.v1_validation?.percent || 0}%
                    </p>
                    <p className="text-xs text-zinc-500">
                      {governanceData.v1_stats?.ok || 0}/{governanceData.v1_stats?.total_tests || 0} tests OK
                    </p>
                  </div>
                </div>
                {governanceData.v1_validation?.blocking_modules?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {governanceData.v1_validation.blocking_modules.map((mod, idx) => (
                      <Badge key={idx} className="bg-red-500/20 text-red-400 border-red-500/30">
                        {mod}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Légende des statuts */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-sm font-medium text-green-400">Exploitable</p>
                <p className="text-xs text-zinc-500">100% tests OK</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <XCircle className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-sm font-medium text-red-400">À corriger</p>
                <p className="text-xs text-zinc-500">KO/NON IMPL. - Bloquant</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <div>
                <p className="text-sm font-medium text-yellow-400">Avec friction</p>
                <p className="text-xs text-zinc-500">OK mais imparfait</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-zinc-700/50 border border-zinc-600/30 rounded-lg">
              <Lock className="w-5 h-5 text-zinc-400" />
              <div>
                <p className="text-sm font-medium text-zinc-400">Non gouvernable</p>
                <p className="text-xs text-zinc-500">V2 ou désactivé</p>
              </div>
            </div>
          </div>

          {/* Modules par catégorie */}
          {loadingGovernance ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
              <span className="ml-3 text-zinc-400">Chargement de la gouvernance...</span>
            </div>
          ) : governanceData ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 🟢 EXPLOITABLE */}
              <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-green-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      Exploitable
                    </span>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      {governanceData.summary?.exploitable_count || 0}
                    </Badge>
                  </CardTitle>
                  <CardDescription>100% tests OK - Production ready</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {governanceData.governance?.exploitable?.length > 0 ? (
                      governanceData.governance.exploitable.map((mod, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-green-500/5 border border-green-500/20 rounded">
                          <div>
                            <p className="font-medium text-white text-sm">{mod.module_label}</p>
                            <p className="text-xs text-zinc-500">{mod.hub}</p>
                          </div>
                          <div className="text-right">
                            <Badge className="bg-green-500/20 text-green-400 text-xs">
                              {mod.tests?.ok}/{mod.tests?.total} OK
                            </Badge>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-zinc-500 text-sm text-center py-4">Aucun module</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* 🔴 À CORRIGER */}
              <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-red-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-red-400" />
                      À corriger
                    </span>
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                      {governanceData.summary?.a_corriger_count || 0}
                    </Badge>
                  </CardTitle>
                  <CardDescription>KO/NON IMPL. - Bloquant V1 - CTO</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {governanceData.governance?.a_corriger?.length > 0 ? (
                      governanceData.governance.a_corriger.map((mod, idx) => (
                        <div key={idx} className="p-2 bg-red-500/5 border border-red-500/20 rounded">
                          <div className="flex items-center justify-between mb-1">
                            <div>
                              <p className="font-medium text-white text-sm">{mod.module_label}</p>
                              <p className="text-xs text-zinc-500">{mod.hub}</p>
                            </div>
                            <div className="flex gap-1">
                              {mod.tests?.ko > 0 && (
                                <Badge className="bg-red-500/20 text-red-400 text-xs">
                                  {mod.tests.ko} KO
                                </Badge>
                              )}
                              {mod.tests?.non_impl > 0 && (
                                <Badge className="bg-zinc-700 text-zinc-300 text-xs">
                                  {mod.tests.non_impl} NON IMPL.
                                </Badge>
                              )}
                            </div>
                          </div>
                          {mod.blocking_tests?.length > 0 && (
                            <div className="mt-2 pl-2 border-l-2 border-red-500/30">
                              {mod.blocking_tests.slice(0, 3).map((test, tidx) => (
                                <p key={tidx} className="text-xs text-red-300">
                                  • {test.label}
                                </p>
                              ))}
                              {mod.blocking_tests.length > 3 && (
                                <p className="text-xs text-zinc-500">
                                  +{mod.blocking_tests.length - 3} autres...
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4">
                        <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                        <p className="text-green-400 text-sm">Aucun module à corriger</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* 🟡 AVEC FRICTION */}
              <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-yellow-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      Avec friction
                    </span>
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                      {governanceData.summary?.avec_friction_count || 0}
                    </Badge>
                  </CardTitle>
                  <CardDescription>OK mais usage imparfait - Acceptable V1</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {governanceData.governance?.avec_friction?.length > 0 ? (
                      governanceData.governance.avec_friction.map((mod, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-yellow-500/5 border border-yellow-500/20 rounded">
                          <div>
                            <p className="font-medium text-white text-sm">{mod.module_label}</p>
                            <p className="text-xs text-zinc-500">{mod.hub}</p>
                          </div>
                          <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                            Backlog produit
                          </Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-zinc-500 text-sm text-center py-4">Aucun module</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* ⚫ NON GOUVERNABLE */}
              <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-zinc-600">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Lock className="w-5 h-5 text-zinc-400" />
                      Non gouvernable
                    </span>
                    <Badge className="bg-zinc-700 text-zinc-300 border-zinc-600">
                      {governanceData.summary?.non_gouvernable_count || 0}
                    </Badge>
                  </CardTitle>
                  <CardDescription>V2 ou service désactivé - Hors périmètre V1</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {governanceData.governance?.non_gouvernable?.length > 0 ? (
                      governanceData.governance.non_gouvernable.map((mod, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-zinc-800/50 border border-zinc-700 rounded">
                          <div>
                            <p className="font-medium text-zinc-300 text-sm">{mod.module_label}</p>
                            <p className="text-xs text-zinc-500">{mod.hub}</p>
                            {mod.archive_reason && (
                              <p className="text-xs text-amber-400/70 mt-1">{mod.archive_reason}</p>
                            )}
                          </div>
                          <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400">
                            V2
                          </Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-zinc-500 text-sm text-center py-4">Aucun module</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-8 text-center">
                <Lock className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">Aucune donnée de gouvernance disponible</p>
                <Button onClick={loadGovernanceSummary} className="mt-4 bg-amber-600 hover:bg-amber-700">
                  Charger les données
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Synthèse Statistiques V1 */}
          {governanceData && (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-cyan-400" />
                  Synthèse V1
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-green-500/10 rounded-lg">
                    <p className="text-3xl font-bold text-green-400">{governanceData.summary?.exploitable_count || 0}</p>
                    <p className="text-sm text-zinc-400">Exploitables</p>
                    <p className="text-xs text-green-400 mt-1">Production ready</p>
                  </div>
                  <div className="text-center p-4 bg-red-500/10 rounded-lg">
                    <p className="text-3xl font-bold text-red-400">{governanceData.summary?.a_corriger_count || 0}</p>
                    <p className="text-sm text-zinc-400">À corriger</p>
                    <p className="text-xs text-red-400 mt-1">Bloquant CTO</p>
                  </div>
                  <div className="text-center p-4 bg-yellow-500/10 rounded-lg">
                    <p className="text-3xl font-bold text-yellow-400">{governanceData.summary?.avec_friction_count || 0}</p>
                    <p className="text-sm text-zinc-400">Avec friction</p>
                    <p className="text-xs text-yellow-400 mt-1">Backlog produit</p>
                  </div>
                  <div className="text-center p-4 bg-zinc-700/30 rounded-lg">
                    <p className="text-3xl font-bold text-zinc-400">{governanceData.summary?.non_gouvernable_count || 0}</p>
                    <p className="text-sm text-zinc-400">Non gouvernable</p>
                    <p className="text-xs text-zinc-500 mt-1">V2 / Désactivé</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB: Mode Conversationnel (Chat) */}
        <TabsContent value="chat">
          <CxOChatPanel cxoType="cto" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
